import type { DocumentDiff, SummaryEntry } from '@westhetics/legal'
import type { DiffViewRow } from '@westhetics/ui'

/** A diferença calculada pelo `@westhetics/legal`, no formato que o `DiffView` desenha. */
export function toDiffRows(diff: DocumentDiff): DiffViewRow[] {
  return diff.lines.map((line) => {
    if (line.op === 'changed') {
      return {
        type: 'changed',
        before: {
          line: line.beforeLine ?? 0,
          parts: (line.beforeParts ?? []).map((part) => ({
            text: part.text,
            changed: part.op !== 'equal',
          })),
        },
        after: {
          line: line.afterLine ?? 0,
          parts: (line.afterParts ?? []).map((part) => ({
            text: part.text,
            changed: part.op !== 'equal',
          })),
        },
      }
    }
    return {
      type: line.op,
      before:
        line.before === null
          ? null
          : { line: line.beforeLine ?? 0, parts: [{ text: line.before }] },
      after:
        line.after === null ? null : { line: line.afterLine ?? 0, parts: [{ text: line.after }] },
    }
  })
}

const STATUS_LABELS: Record<SummaryEntry['status'], Record<SummaryEntry['kind'], string>> = {
  added: { section: 'nova', block: 'novo', variable: 'entrou', front_matter: 'entrou' },
  removed: { section: 'saiu', block: 'saiu', variable: 'saiu', front_matter: 'saiu' },
  changed: {
    section: 'alterada',
    block: 'alterado',
    variable: 'alterada',
    front_matter: 'alterado',
  },
  moved: {
    section: 'mudou de lugar',
    block: 'mudou de lugar',
    variable: 'mudou',
    front_matter: 'mudou',
  },
}

const KIND_LABELS: Record<SummaryEntry['kind'], string> = {
  section: 'Cláusula',
  block: 'Bloco',
  variable: 'Variável',
  front_matter: 'Identificação',
}

/** "alterada", "nova", "saiu": o que aconteceu com a cláusula, o bloco ou a variável. */
export function summaryStatusLabel(entry: SummaryEntry) {
  return STATUS_LABELS[entry.status][entry.kind]
}

export function summaryKindLabel(entry: SummaryEntry) {
  return KIND_LABELS[entry.kind]
}

/** "3 cláusulas alteradas · 1 nova · 1 variável saiu": a linha que resume a comparação. */
export function diffHeadline(diff: DocumentDiff) {
  if (diff.isIdentical) return 'Os dois textos são iguais.'
  const { addedLines, removedLines, changedLines } = diff.stats
  const parts: string[] = []
  const plural = (count: number, one: string, many: string) =>
    `${count} ${count === 1 ? one : many}`
  if (changedLines) parts.push(plural(changedLines, 'linha alterada', 'linhas alteradas'))
  if (addedLines) parts.push(plural(addedLines, 'linha nova', 'linhas novas'))
  if (removedLines) parts.push(plural(removedLines, 'linha removida', 'linhas removidas'))
  return parts.join(' · ')
}
